import { newDataKey } from '../crypto/vault.ts';
import { wrapDataKey } from '../crypto/session.ts';
import { registerAttachment, uploadEncrypted } from '../data/attachments.ts';
import { api } from '../data/api.ts';
import { store } from '../data/db.ts';
import { acquire, flushPersist, onDocsChanged, release, withDoc } from '../data/docs.ts';
import { emptyNote, newId } from '../data/notes.ts';
import { extract, findDate, firstLine } from './extract.ts';
import { pageCount, recognize, recognizeRemote, releaseEngine, type Progress } from './ocr.ts';
import { renderThumb } from './thumb.ts';
import { attachmentBytes, listAttachments } from '../data/attachments.ts';
import type { DocumentMeta, LocalNote } from '../data/types.ts';

/**
 * Posteingang und Archiv (docs/KONZEPT.md, Abschnitt 19).
 *
 * Ein Dokument ist technisch eine Notiz — dieselbe Entscheidung wie bei den
 * Erinnerungen: Verschlüsselung, Abgleich, Offline-Warteschlange, Versionen und
 * Papierkorb gibt es damit unverändert, ohne eine zweite Maschinerie.
 *
 * Im Markdown stehen Titel, eigene Notiz und Schlagwörter — das ist der Teil, den man
 * ändern darf. Die Datei hängt als Anhang daran und wird nicht angefasst; der
 * gewonnene Text liegt daneben in `meta.dokument`, weil er abgeleitet ist und beim
 * Schreiben nicht im Weg stehen soll.
 */

export type Status = DocumentMeta['status'];

/** Ein Dokument samt seiner Zeile — der Bereich arbeitet nur mit diesen. */
export type ArchiveEntry = LocalNote & { dokument: DocumentMeta };

/**
 * `!= null` statt `!== null`, und das ist keine Kosmetik: Notizzeilen, die vor
 * dieser Fassung angelegt wurden, haben das Feld gar nicht. `undefined !== null`
 * ist wahr — damit galt jede alte Notiz als Dokument, und der erste Zugriff auf
 * `dokument.status` hat die App nach dem Entsperren weiß gemacht.
 */
export function isDocument(note: LocalNote): note is ArchiveEntry {
  return note.dokument != null;
}

/** Trennt Dokumente von Notizen. Die Notizliste zeigt nur, was keins ist. */
export function documentsOf(notes: LocalNote[], status?: Status): ArchiveEntry[] {
  const found = notes.filter(isDocument);
  const wanted = status ? found.filter((entry) => entry.dokument.status === status) : found;
  // Neuestes zuerst — nach Dokumentdatum, ersatzweise nach Eingang.
  return wanted.sort(
    (a, b) =>
      (b.dokument.datum ?? b.dokument.eingang) - (a.dokument.datum ?? a.dokument.eingang),
  );
}

async function patchMeta(id: string, change: Partial<DocumentMeta>): Promise<void> {
  await withDoc(id, (entry) => {
    const current = (entry.meta.get('dokument') as DocumentMeta | undefined) ?? null;
    if (!current) {
      // Stillschweigend nichts zu tun war der Grund, warum ein Stapellauf Lücken
      // ließ, ohne dass jemand etwas merkte.
      console.warn('[archiv] Änderung verworfen, Dokument nicht lesbar:', id);
      return;
    }
    entry.meta.set('dokument', { ...current, ...change });
  });
  // Sofort schreiben statt in 250 Millisekunden: Ein Stapellauf berührt hunderte
  // Dokumente kurz hintereinander, und was dabei nur in der Warteschlange steht,
  // geht bei jedem Aufräumen dazwischen verloren.
  await flushPersist(id);
}

/**
 * Nach einer Erkennung ist die erste Zeile des Textes fast immer die bessere
 * Überschrift als `IMG_4711`. Angefasst wird sie aber nur, solange sie noch der
 * Dateiname ist — was jemand selbst geschrieben hat, bleibt stehen.
 */
async function retitle(id: string, fileName: string, text: string): Promise<void> {
  const headline = firstLine(text);
  if (!headline) return;
  const fallback = `# ${fileName.replace(/\.[^.]+$/, '')}`;

  await withDoc(id, (entry) => {
    const content = entry.text.toString();
    const end = content.indexOf('\n');
    const first = end < 0 ? content : content.slice(0, end);
    if (first.trim() !== fallback) return;
    entry.doc.transact(() => {
      entry.text.delete(0, first.length);
      entry.text.insert(0, `# ${headline}`);
    });
  });
  await flushPersist(id);
}

type Recognizer = (
  bytes: Uint8Array,
  mime: string,
  name: string,
  options: { onProgress?: (progress: Progress) => void },
) => Promise<{ text: string; confidence: number; pages: number }>;

/** Beide Wege enden gleich: Text ablegen, Überschrift und Datum nachziehen. */
async function runRecognition(
  id: string,
  options: { bytes?: Uint8Array; hatDatum: boolean; onProgress?: (progress: Progress) => void },
  recognizer: Recognizer,
  quelle: DocumentMeta['quelle'],
): Promise<{ confidence: number } | null> {
  const attachments = await listAttachments(id);
  const first = Object.entries(attachments)[0];
  if (!first) {
    // Das ist kein Erkennungsproblem, sondern ein fehlendes Dokument. Wer hier
    // „kein Text gefunden" meldet, schickt die Suche in die falsche Richtung.
    throw new Error('An diesem Dokument hängt keine Datei — der Upload ist gescheitert.');
  }
  const [attachmentId, file] = first;

  const data = options.bytes ?? (await attachmentBytes(id, attachmentId))?.bytes;
  if (!data) {
    throw new Error(`Die Datei „${file.name}" ist auf dem Server nicht abrufbar.`);
  }

  const result = await recognizer(data, file.mime, file.name, { onProgress: options.onProgress });
  // Die Datei ist ohnehin geladen: Was noch kein Deckblatt hat, bekommt jetzt eins.
  const nachtrag = await renderThumb(data, file.mime, file.name);
  if (nachtrag) await patchMeta(id, { vorschau: nachtrag });
  if (result.text === '') {
    console.warn('[archiv] Erkennung ohne Text:', id, file.name);
    return null;
  }

  await retitle(id, file.name, result.text);
  await patchMeta(id, {
    erkannt: result.text,
    quelle,
    konfidenz: quelle === 'ocr' ? result.confidence : null,
    // Ein Datum, das vorher niemand kannte, steht jetzt vielleicht im Text.
    ...(options.hatDatum ? {} : { datum: findDate(result.text) }),
  });
  return { confidence: result.confidence };
}

/** Prüfsumme der Datei — Grundlage der Dublettenprüfung. */
async function digest(bytes: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Die Prüfsummen dessen, was schon da ist — für einen Stapellauf einmal gebildet.
 *
 * Was im Papierkorb liegt, zählt nicht: Wer ein Dokument weggeworfen hat, will es
 * erneut aufnehmen können. Andernfalls sagt die App „schon vorhanden" und meint eine
 * Leiche, die man nirgends sieht.
 */
export function knownHashes(documents: ArchiveEntry[]): Set<string> {
  return new Set(
    documents
      .filter((entry) => entry.deletedAt === null)
      .map((entry) => entry.dokument.hash)
      .filter((hash): hash is string => !!hash),
  );
}

/**
 * Aus welchen Dokumenten sich überhaupt ein Deckblatt gewinnen lässt.
 *
 * Ohne diese Frage stünde über einem Posteingang voller E-Mails und Textdateien für
 * immer „drei ohne Deckblatt" — ein Hinweis, dem niemand nachkommen kann. Die
 * Herkunft des Textes verrät es: Was aus einer E-Mail, einer Textdatei oder einem
 * Word-Dokument kam, ist kein Blatt Papier. Alles andere ist Bild oder PDF.
 */
export function kannDeckblatt(entry: ArchiveEntry): boolean {
  return !['text', 'eml', 'docx'].includes(entry.dokument.quelle);
}

/**
 * Wem noch ein Deckblatt fehlt — und wer eines bekommen könnte. Was schon einmal
 * kein Bild hergab, zählt nicht mehr mit; erzwingen lässt es sich trotzdem.
 */
export function brauchtDeckblatt(entry: ArchiveEntry): boolean {
  return (
    kannDeckblatt(entry) && entry.dokument.vorschau === null && entry.dokument.bildlos !== true
  );
}

export const archive = {
  /**
   * Nimmt eine Datei auf. Der Text wird gewonnen, wo Text ist; was Pixel bleibt,
   * bekommt `quelle: 'keine'` und wartet auf die Erkennung.
   *
   * Bewusst ohne `prepare()`: Bilder in Notizen werden verkleinert, ein Beleg nicht.
   * Bei einem amtlichen Dokument zählt das Original, und Kleingedrucktes überlebt
   * eine Neukomprimierung schlecht.
   */
  async add(
    file: File,
    known?: Set<string>,
    /** Meldet, woran gerade gearbeitet wird — ein Upload dauert, das darf man sehen. */
    onStage?: (stufe: 'lesen' | 'hochladen') => void,
  ): Promise<{ id: string; meta: DocumentMeta; bytes: Uint8Array } | { duplicate: true }> {
    onStage?.('lesen');
    const bytes = new Uint8Array(await file.arrayBuffer());
    const hash = await digest(bytes);
    // Beim zweiten Lauf über denselben Ordner soll nichts doppelt liegen.
    if (known?.has(hash)) return { duplicate: true };
    known?.add(hash);

    const found = await extract(file.name, file.type, bytes);

    const now = Date.now();
    const id = newId();
    // Die Zeile muss vor dem ersten Delta stehen — sonst nimmt der Server nichts an.
    await store.putNote({ ...emptyNote(id, wrapDataKey(newDataKey()), now), dirty: true });

    const meta: DocumentMeta = {
      eingang: now,
      datum: found.datum,
      status: 'posteingang',
      erkannt: found.text,
      quelle: found.quelle,
      konfidenz: null,
      hash,
      unvollstaendig: true,
      vorschau: null,
      // Die Seitenzahl wird vor der Erkennung gebraucht: Bei einem Stapel wird
      // gefragt, bevor das Telefon minutenlang rechnet.
      seiten: found.quelle === 'keine' ? await pageCount(bytes, file.type, file.name) : null,
      noteId: null,
    };

    const entry = await acquire(id);
    entry.doc.transact(() => {
      entry.meta.set('createdAt', now);
      entry.meta.set('pinned', false);
      entry.meta.set('folderId', null);
      entry.meta.set('dokument', meta);
      entry.text.insert(0, `# ${found.titel ?? file.name}\n\n`);
    });
    release(id);

    try {
      onStage?.('hochladen');
      const uploaded = await uploadEncrypted(
        bytes,
        file.type || 'application/octet-stream',
        file.name,
        id,
      );
      await registerAttachment(id, uploaded.id, uploaded.meta);

      // Nachsehen statt hoffen. Der Eintrag ist der einzige Weg zurück zur Datei;
      // ging er verloren, entsteht ein Dokument, das aussieht wie ein Dokument und
      // keines ist. Genau das ist passiert — und niemand hat es gemerkt.
      const angekommen = await listAttachments(id);
      if (!angekommen[uploaded.id]) {
        throw new Error('Die Datei wurde hochgeladen, aber nicht am Dokument vermerkt.');
      }
      // Das Deckblatt entsteht hier, wo die Bytes ohnehin zur Hand sind.
      const vorschau = await renderThumb(bytes, file.type, file.name);
      await patchMeta(id, { unvollstaendig: false, vorschau, bildlos: vorschau === null });
    } catch (error) {
      // Ein Dokument ohne seine Datei ist eine Hülle: Sie steht im Posteingang, lässt
      // sich ablegen, erkennen und suchen — und ist doch leer. Lieber ganz weg, dann
      // ist der Fehler eindeutig und der zweite Versuch sauber.
      await store.removeNote(id);
      throw error;
    }

    // Die Bytes wandern mit zurück: Wer gleich erkennen will, soll sie nicht noch
    // einmal vom Server holen müssen.
    return { id, meta, bytes };
  },

  /**
   * In den Papierkorb — dieselbe Frist wie bei Notizen. Umkehrbar, solange die Zeile
   * noch da ist.
   */
  async remove(id: string): Promise<void> {
    const now = Date.now();
    await store.patchNote(id, (note) => ({ ...note, deletedAt: now, updatedAt: now, dirty: true }));
  },

  async restore(id: string): Promise<void> {
    const now = Date.now();
    await store.patchNote(id, (note) => ({ ...note, deletedAt: null, updatedAt: now, dirty: true }));
  },

  /**
   * Endgültig: Der Server bekommt die Löschung, die Zeile verschwindet hier sofort.
   * Für Dokumente, die gar nicht erst richtig angekommen sind, ist der Umweg über
   * eine Frist nur im Weg.
   */
  async purge(id: string): Promise<void> {
    await api.deleteNote(id).catch(() => undefined);
    await store.removeNote(id);
  },

  /** Vom Posteingang ins Archiv und zurück. Nichts wandert von selbst. */
  setStatus: (id: string, status: Status) => patchMeta(id, { status }),

  /** Das Datum im Dokument — der Vorschlag ist oft richtig, aber nicht immer. */
  setDate: (id: string, datum: number | null) => patchMeta(id, { datum }),

  /** Ergebnis einer Erkennung. Ersetzt, was vorher da war. */
  setText: (
    id: string,
    erkannt: string,
    quelle: DocumentMeta['quelle'],
    konfidenz: number | null = null,
  ) => patchMeta(id, { erkannt, quelle, konfidenz }),

  /**
   * Erkennt die Datei eines Dokuments auf dem Gerät. `bytes` spart den Umweg über
   * den Server, wenn sie ohnehin gerade zur Hand sind — direkt nach dem Aufnehmen.
   */
  async recognize(
    id: string,
    options: {
      /** Spart den Umweg über den Server, wenn die Datei ohnehin zur Hand ist. */
      bytes?: Uint8Array;
      /** Ist schon ein Datum bekannt, wird es nicht überschrieben. */
      hatDatum: boolean;
      onProgress?: (progress: Progress) => void;
    },
  ): Promise<{ confidence: number } | null> {
    return runRecognition(id, options, recognize, 'ocr');
  },

  /**
   * Erkennung über Claude. Der einzige Weg, auf dem ein Dokument das Gerät verlässt —
   * und er wird nur gegangen, wenn jemand ihn ausdrücklich wählt.
   */
  async recognizeRemote(
    id: string,
    options: { bytes?: Uint8Array; hatDatum: boolean; onProgress?: (progress: Progress) => void },
  ): Promise<{ confidence: number } | null> {
    return runRecognition(id, options, recognizeRemote, 'claude');
  },

  /**
   * Erzeugt das Deckblatt neu. Nötig, weil ein Bild beim Aufnehmen scheitern kann,
   * ohne dass es auffällt: Ein PDF, dessen erste Seite noch nicht fertig gerendert
   * war, oder ein Format, das der Browser damals nicht öffnen konnte. Die Kachel
   * zeigt dann Text statt Bild, und niemand weiß, warum.
   *
   * Auch ein Misserfolg wird geschrieben (`vorschau: null`). Ein altes, falsches
   * Deckblatt stehen zu lassen wäre schlechter als die ehrliche Textkachel.
   */
  async rethumb(id: string): Promise<boolean> {
    const attachments = await listAttachments(id);
    const first = Object.entries(attachments)[0];
    if (!first) {
      throw new Error('An diesem Dokument hängt keine Datei — es gibt nichts abzubilden.');
    }
    const [attachmentId, file] = first;
    const data = (await attachmentBytes(id, attachmentId))?.bytes;
    if (!data) {
      throw new Error(`Die Datei „${file.name}" ist auf dem Server nicht abrufbar.`);
    }
    const vorschau = await renderThumb(data, file.mime, file.name);
    // Auch der Misserfolg wird vermerkt, sonst mahnt die Übersicht ihn ewig an.
    await patchMeta(id, { vorschau, bildlos: vorschau === null });
    return vorschau !== null;
  },

  /** Das Modell hält Speicher fest; nach getaner Arbeit darf es gehen. */
  release: releaseEngine,

  onChange(listener: () => void): () => void {
    return onDocsChanged(() => listener());
  },
};
